/**
 * Integration Onboarding component
 * Displays step-by-step instructions for connecting to tax software integration
 * This is shown when user clicks on the "Integration" onboarding step
 */

import React, { useEffect } from "react";
import { MantineProvider } from "@mantine/core";
import { IntegrationGuide } from "@components/Integrations";
import type { IntegrationMetadata } from "../types";
import type { IntegrationType } from "@components/Integrations/types";
import { useTaxOfficeStore } from "@pages/Onboarding/stores/taxOfficeStore";
import { getTaxSoftwareDisplayName } from "@pages/Onboarding/constants";

interface IntegrationOnboardingProps {
  metadata?: IntegrationMetadata;
  onComplete?: () => void;
}

/**
 * Integration onboarding workflow component
 * Shows tailored instructions for each integration platform
 */
const IntegrationOnboarding: React.FC<IntegrationOnboardingProps> = ({
  metadata,
  onComplete,
}) => {
  const taxOfficeData = useTaxOfficeStore((state) => state.data);
  const fetchTaxOfficeData = useTaxOfficeStore((state) => state.fetchData);

  // Fetch tax office data to determine software type
  useEffect(() => {
    if (!taxOfficeData) {
      fetchTaxOfficeData();
    }
  }, [taxOfficeData, fetchTaxOfficeData]);

  const integrationName =
    metadata?.integrationName ||
    getTaxSoftwareDisplayName(taxOfficeData?.softwareType);

  // Map integration names to IntegrationType
  const getIntegrationType = (name: string): IntegrationType => {
    // Normalize the name
    const normalized = name.toLowerCase().trim();

    if (normalized.includes("jotform")) return "Jotform";
    if (normalized.includes("cognito")) return "Cognito Forms";
    if (normalized.includes("high level") || normalized.includes("ghl"))
      return "Go High Level";
    if (normalized.includes("suitedash")) return "SuiteDash";

    // Default to Jotform if unknown
    return "Jotform";
  };

  const integrationType = getIntegrationType(integrationName);

  return (
    <MantineProvider forceColorScheme="light">
      <IntegrationGuide
        integrationType={integrationType}
        onComplete={onComplete}
      />
    </MantineProvider>
  );
};

export default IntegrationOnboarding;
